<template>
  <div>
    <!-- 提示模式 -->
    <div v-if="visible && type === 'toast'" class="toast-container">
      <div class="toast-message">
        {{ message }}
      </div>
    </div>

    <!-- 弹窗模式 -->
    <div v-else-if="visible && type === 'modal'" class="modal-overlay" :class="{ 'non-modal': !modal }" @click="onOverlayClick">
      <div class="modal-container" @click.stop>
        <div class="modal-header" v-if="showHeader">
          <h3>{{ title }}</h3>
          <button class="modal-close-button" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <p>{{ message }}</p>
        </div>
        <div class="modal-footer" v-if="showFooter">
          <button class="modal-confirm-button" @click="confirm">{{ confirmText }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '提示'
    },
    message: {
      type: String,
      default: ''
    },
    confirmText: {
      type: String,
      default: '确定'
    },
    type: {
      type: String,
      default: 'modal', // 'modal' 或 'toast'
      validator: (value) => ['modal', 'toast'].includes(value)
    },
    modal: {
      type: Boolean,
      default: true // 仅对modal类型有效，true为模态，false为非模态
    },
    duration: {
      type: Number,
      default: 5000 // 仅对toast类型有效，单位毫秒
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: true
    }
  },
  emits: ['confirm', 'close'],
  data() {
    return {
      timer: null
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.startTimer();
      } else {
        this.clearTimer();
      }
    },
    type(newVal) {
      // 当类型改变时，如果可见且是toast类型，重新启动定时器
      if (this.visible && newVal === 'toast') {
        this.startTimer();
      }
    }
  },
  methods: {
    closeModal() {
      this.clearTimer();
      this.$emit('close');
    },
    confirm() {
      this.$emit('confirm');
      this.closeModal();
    },
    onOverlayClick() {
      // 只有在模态模式下点击遮罩才关闭
      if (this.modal || this.type === 'toast') {
        this.closeModal();
      }
    },
    startTimer() {
      // 如果是提示模式，设置定时器自动关闭
      if (this.type === 'toast') {
        this.clearTimer();
        // 添加一些调试信息
        console.log('Starting timer for toast with duration:', this.duration);
        this.timer = setTimeout(() => {
          console.log('Toast timer expired, closing modal');
          this.closeModal();
        }, this.duration);
      }
    },
    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }
  },
  beforeUnmount() {
    this.clearTimer();
  }
};
</script>

<style scoped>
.modal-overlay {
  /* 定义CSS变量 */
  --border-radius: 4px;
  --box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  --hover-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  --active-shadow: 0 1px 5px rgba(0, 0, 0, 0.1);
  --primary-color4: #ff5f5f;
  --primary-color-dark: #e29a9a;
  --primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --background: #ffffff;
  --font-color: #333333;
  --transition: all 0.3s ease;
  
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  overflow: hidden;
  max-width: 500px;
  width: 90%;
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  background-color: var(--primary-color4);
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
}

.modal-header h3 {
  margin: 0;
  color: white;
  font-weight: bold;
}

.modal-close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: white;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: var(--transition);
}

.modal-close-button:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 20px;
  background-color: var(--background);
}

.modal-body p {
  margin: 0;
  color: var(--font-color);
  font-size: 1rem;
  line-height: 1.5;
}

.modal-footer {
  padding: 15px 20px;
  background-color: var(--background);
  border-top: 1px solid #ddd;
  text-align: right;
}

.modal-confirm-button {
  padding: 10px 20px;
  border: none;
  border-radius: var(--border-radius);
  background: var(--primary-gradient);
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  box-shadow: var(--box-shadow);
}

.modal-confirm-button:hover {
  background: var(--primary-color-dark);
  transform: translateY(-2px);
  box-shadow: var(--hover-shadow);
}

.modal-confirm-button:active {
  transform: translateY(0);
  box-shadow: var(--active-shadow);
}

/* 提示模式样式 */
.toast-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  animation: toastFadeIn 0.3s ease-out;
}

@keyframes toastFadeIn {
  from {
    opacity: 0;
    transform: translate(-50%, -20px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

.toast-message {
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 12px 20px;
  border-radius: var(--border-radius);
  font-size: 1rem;
  box-shadow: var(--box-shadow);
  backdrop-filter: blur(10px);
}

/* 非模态弹窗样式 */
.modal-overlay.non-modal {
  background-color: transparent;
  pointer-events: none;
}

.modal-overlay.non-modal .modal-container {
  pointer-events: auto;
}

/* 确保标题文字颜色为白色 */
.modal-header h3 {
  margin: 0;
  color: white !important;
  font-weight: bold;
}

/* 确保关闭按钮颜色为白色 */
.modal-close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: white !important;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: var(--transition);
}

/* 确保确认按钮文字颜色为白色 */
.modal-confirm-button {
  padding: 10px 20px;
  border: none;
  border-radius: var(--border-radius);
  background: var(--primary-gradient);
  color: white !important;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  box-shadow: var(--box-shadow);
}
</style>
